import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Switch,
  FormControlLabel,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Fastfood as DishIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  FileCopy as CopyIcon,
} from '@mui/icons-material';
import { useAppStore } from '../store/appStore';
import { productService } from '../services/productService';
import { colors } from '../theme/theme';
import { ConfirmModal } from '../components/common/ConfirmModal';
import toast from 'react-hot-toast';

const CategoryDishes = () => {
  const navigate = useNavigate();
  const { menuId, categoryId } = useParams();
  const { branch: selectedBranch } = useAppStore();

  const [menuType, setMenuType] = useState(null);
  const [category, setCategory] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dishDialogOpen, setDishDialogOpen] = useState(false);
  const [newDish, setNewDish] = useState({
    name: '',
    code: '',
    price: '',
    description: '',
    is_veg: true,
    category: categoryId ? Number(categoryId) : '',
    menuTypes: menuId ? [Number(menuId)] : [],
  });
  const [savingDish, setSavingDish] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [togglingItem, setTogglingItem] = useState(null);
  const [copyDishDialogOpen, setCopyDishDialogOpen] = useState(false);
  const [copyDishSource, setCopyDishSource] = useState(null);
  const [copyDishTargetMenu, setCopyDishTargetMenu] = useState('');
  const [copyingDish, setCopyingDish] = useState(false);
  const [availableMenusForDishCopy, setAvailableMenusForDishCopy] = useState([]);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategoryTargetMenu, setBulkCategoryTargetMenu] = useState('');
  const [bulkCategoryInProgress, setBulkCategoryInProgress] = useState(false);
  const [availableMenusForBulkCategoryOperation, setAvailableMenusForBulkCategoryOperation] = useState([]);
  const [selectedDishes, setSelectedDishes] = useState(new Set());
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dishToDelete, setDishToDelete] = useState(null);

  useEffect(() => {
    if (!menuId || !categoryId) {
      navigate('/dish-access');
      return;
    }

    setLoading(true);
    setError('');

    const loadData = async () => {
      try {
        // Fetch menu type details
        const menuResponse = await productService.getMenu(menuId);
        if (!menuResponse?.data?.success) {
          throw new Error('Menu type not found');
        }
        setMenuType(menuResponse.data.data);

        // Fetch category details
        const categoryResponse = await productService.getCategory(categoryId);
        if (!categoryResponse?.data?.success) {
          throw new Error('Category not found');
        }
        setCategory(categoryResponse.data.data);

        // Fetch branch categories so the add dialog can choose a category
        if (selectedBranch?.id) {
          const categoriesResponse = await productService.getCategories({ branch: selectedBranch.id });
          if (categoriesResponse?.data?.success) {
            setCategoryOptions(categoriesResponse.data.data || []);
          }

          const menusResponse = await productService.getMenus({ branch: selectedBranch.id });
          if (menusResponse?.data?.success) {
            setMenuOptions(menusResponse.data.data || []);
          }
        }

        // Fetch dishes for this category
        const dishResponse = await productService.getProducts({ menu: menuId, category: categoryId });

        if (dishResponse?.data?.success) {
          setDishes(dishResponse.data.data || []);
        } else {
          throw new Error(dishResponse?.data?.message || 'Unable to load dishes');
        }
      } catch (fetchError) {
        console.error('Error loading category dishes:', fetchError);
        setError(fetchError.message || 'Failed to load category dishes.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [menuId, categoryId, navigate, selectedBranch]);

  const refreshDishes = async () => {
    try {
      const dishResponse = await productService.getProducts({ menu: menuId, category: categoryId });
      if (dishResponse?.data?.success) {
        setDishes([...(dishResponse.data.data || [])]);
      }
    } catch (err) {
      console.error('Refresh dishes failed:', err);
    }
  };

  const handleCreateDish = async () => {
    if (!newDish.name.trim() || !newDish.price) {
      toast.error('Dish name and price are required');
      return;
    }

    if (!selectedBranch?.id || selectedBranch?.isAllOutlets) {
      toast.error('Please set a specific branch before adding dishes');
      return;
    }

    // Check for duplicate dish name in current category
    const dishName = newDish.name.trim();
    const existingDish = dishes.find(dish => 
      dish.name.toLowerCase() === dishName.toLowerCase()
    );

    if (existingDish) {
      toast.error(`A dish with the name "${dishName}" already exists in this category.`);
      return;
    }

    if (!newDish.category) {
      toast.error('Please select a category');
      return;
    }

    if (!newDish.menuTypes || newDish.menuTypes.length === 0) {
      toast.error('Please select at least one menu type');
      return;
    }

    const selectedMenuIds = Array.from(new Set(newDish.menuTypes.map((id) => Number(id))));
    const primaryMenuId = selectedMenuIds.includes(Number(menuId)) ? Number(menuId) : Number(menuId);

    setSavingDish(true);
    try {
      const response = await productService.createProduct({
        name: dishName,
        price_write: parseFloat(newDish.price),
        code: newDish.code.trim(),
        description: newDish.description.trim(),
        is_veg: newDish.is_veg,
        branch: selectedBranch.id,
        category: newDish.category,
        menu: primaryMenuId,
      });

      if (response?.data?.success) {
        const createdDish = response.data.data;

        const extraMenuIds = selectedMenuIds.filter((id) => id !== primaryMenuId);
        if (extraMenuIds.length > 0) {
          await Promise.all(extraMenuIds.map(async (targetMenuId) => {
            try {
              await productService.copyProduct(createdDish.id, { menu: targetMenuId });
            } catch (copyErr) {
              console.error(`Failed to copy dish to menu ${targetMenuId}:`, copyErr);
            }
          }));
        }

        toast.success('Dish added');
        setNewDish({
          name: '',
          code: '',
          price: '',
          description: '',
          is_veg: true,
          category: newDish.category,
          menuTypes: [Number(menuId)],
        });
        setDishDialogOpen(false);
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to create dish');
      }
    } catch (err) {
      console.error('Create dish error:', err);
      toast.error('Failed to create dish');
    } finally {
      setSavingDish(false);
    }
  };

  const handleEditDish = async () => {
    if (!newDish.name.trim() || !newDish.price) {
      toast.error('Dish name and price are required');
      return;
    }

    // Check for duplicate dish name in current category (excluding current dish)
    const dishName = newDish.name.trim();
    const existingDish = dishes.find(dish => 
      dish.name.toLowerCase() === dishName.toLowerCase() && dish.id !== editingDish.id
    );

    if (existingDish) {
      toast.error(`A dish with the name "${dishName}" already exists in this category.`);
      return;
    }

    setSavingDish(true);
    try {
      const response = await productService.updateProduct(editingDish.id, {
        name: dishName,
        code: newDish.code.trim(),
        price: parseFloat(newDish.price),
        description: newDish.description.trim(),
        is_veg: newDish.is_veg,
      });

      if (response?.data?.success) {
        toast.success('Dish updated');
        setNewDish({ name: '', price: '', description: '', is_veg: true });
        setDishDialogOpen(false);
        setEditingDish(null);
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to update dish');
      }
    } catch (err) {
      console.error('Update dish error:', err);
      toast.error('Failed to update dish');
    } finally {
      setSavingDish(false);
    }
  };

  const handleDeleteDish = async (dishId) => {
    setDeletingItem(dishId);
    try {
      const response = await productService.deleteProduct(dishId);
      if (response.status === 204 || (response?.data?.success)) {
        toast.success('Dish deleted');
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to delete dish');
      }
    } catch (err) {
      console.error('Delete dish error:', err);
      toast.error('Failed to delete dish');
    } finally {
      setDeletingItem(null);
    }
  };

  const handleToggleDish = async (dish) => {
    setTogglingItem(dish.id);
    try {
      const response = await productService.toggleProductStatus(dish.id);
      if (response?.data?.success) {
        toast.success(`Dish ${dish.is_available ? 'deactivated' : 'activated'}`);
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to toggle dish status');
      }
    } catch (err) {
      console.error('Toggle dish error:', err);
      toast.error('Failed to toggle dish status');
    } finally {
      setTogglingItem(null);
    }
  };

  const openEditDishDialog = (dish) => {
    setEditingDish(dish);
    setNewDish({
      name: dish.name,
      code: dish.code || '',
      price: dish.price ? dish.price.toString() : dish.variants[0]?.price.toString() || '',
      description: dish.description || '',
      is_veg: dish.is_veg !== undefined ? dish.is_veg : true,
      category: dish.category || Number(categoryId),
      menuTypes: [Number(menuId)],
    });
    setDishDialogOpen(true);
  };

  const closeDishDialog = () => {
    setDishDialogOpen(false);
    setEditingDish(null);
    setNewDish({
      name: '',
      code: '',
      price: '',
      description: '',
      is_veg: true,
      category: Number(categoryId),
      menuTypes: [Number(menuId)],
    });
  };

  const toggleDishSelection = (dishId) => {
    const newSelected = new Set(selectedDishes);
    if (newSelected.has(dishId)) {
      newSelected.delete(dishId);
    } else {
      newSelected.add(dishId);
    }
    setSelectedDishes(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedDishes.size === dishes.length) {
      setSelectedDishes(new Set());
    } else {
      setSelectedDishes(new Set(dishes.map(d => d.id)));
    }
  };

  const handleBulkActivate = async () => {
    if (selectedDishes.size === 0) {
      toast.error('Please select at least one dish');
      return;
    }

    setBulkOperationInProgress(true);
    try {
      const response = await productService.bulkUpdateProductStatus({
        product_ids: Array.from(selectedDishes),
        is_available: true
      });

      if (response?.data?.success) {
        toast.success(`${response.data.data.updated_count} dish(es) activated`);
        setSelectedDishes(new Set());
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to activate dishes');
      }
    } catch (err) {
      console.error('Bulk activate error:', err);
      toast.error('Failed to activate dishes');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedDishes.size === 0) {
      toast.error('Please select at least one dish');
      return;
    }

    setBulkOperationInProgress(true);
    try {
      const response = await productService.bulkUpdateProductStatus({
        product_ids: Array.from(selectedDishes),
        is_available: false
      });

      if (response?.data?.success) {
        toast.success(`${response.data.data.updated_count} dish(es) deactivated`);
        setSelectedDishes(new Set());
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to deactivate dishes');
      }
    } catch (err) {
      console.error('Bulk deactivate error:', err);
      toast.error('Failed to deactivate dishes');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const fetchMenusForDishCopy = async () => {
    try {
      const menusResponse = await productService.getMenus();
      if (menusResponse?.data?.success) {
        setAvailableMenusForDishCopy(
          (menusResponse.data.data || []).filter((menu) => menu.id !== Number(menuId))
        );
      }
    } catch (err) {
      console.error('Fetch menus failed:', err);
    }
  };

  const fetchMenusForBulkCategoryOperation = async () => {
    try {
      const menusResponse = await productService.getMenus();
      if (menusResponse?.data?.success) {
        setAvailableMenusForBulkCategoryOperation(
          (menusResponse.data.data || []).filter((menu) => menu.id !== Number(menuId))
        );
      }
    } catch (err) {
      console.error('Fetch bulk category menus failed:', err);
      setAvailableMenusForBulkCategoryOperation([]);
    }
  };

  const openBulkCategoryDialog = async () => {
    if (selectedDishes.size === 0) {
      toast.error('Please select at least one dish');
      return;
    }
    setBulkCategoryDialogOpen(true);
    setBulkCategoryTargetMenu('');
    await fetchMenusForBulkCategoryOperation();
  };

  const closeBulkCategoryDialog = () => {
    setBulkCategoryDialogOpen(false);
    setBulkCategoryTargetMenu('');
    setBulkCategoryInProgress(false);
  };

  const handleBulkCategoryOperation = async () => {
    if (!bulkCategoryTargetMenu) {
      toast.error('Select target menu');
      return;
    }

    setBulkCategoryInProgress(true);
    try {
      const response = await productService.bulkCopyToMenu({
        product_ids: Array.from(selectedDishes),
        target_menu: bulkCategoryTargetMenu,
      });

      if (response?.data?.success) {
        const copiedCount = response?.data?.data?.copied_count ?? 0;
        const skippedCount = response?.data?.data?.skipped_count ?? 0;
        
        if (copiedCount > 0) {
          toast.success(`${copiedCount} dish(es) copied successfully${skippedCount > 0 ? `, ${skippedCount} duplicate(s) skipped` : ''}`);
        } else if (skippedCount > 0) {
          toast(`${skippedCount} duplicate(s) skipped - all selected dishes already exist in target menu`);
        }
        
        setSelectedDishes(new Set());
        closeBulkCategoryDialog();
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to copy dishes');
      }
    } catch (err) {
      console.error('Bulk copy operation failed:', err);
      toast.error('Failed to copy dishes');
    } finally {
      setBulkCategoryInProgress(false);
    }
  };

  const openCopyDishDialog = async (dish) => {
    setCopyDishSource(dish);
    setCopyDishDialogOpen(true);
    await fetchMenusForDishCopy();
  };

  const closeCopyDishDialog = () => {
    setCopyDishDialogOpen(false);
    setCopyDishSource(null);
    setCopyDishTargetMenu('');
  };

  const handleCopyDish = async () => {
    if (!copyDishTargetMenu) {
      toast.error('Select a menu to copy the dish to');
      return;
    }

    setCopyingDish(true);
    try {
      const response = await productService.copyProduct(copyDishSource.id, {
        menu: copyDishTargetMenu,
      });

      if (response?.data?.success) {
        toast.success(`Dish copied successfully to target menu`);
        closeCopyDishDialog();
        await refreshDishes();
      } else {
        toast.error(response?.data?.message || 'Failed to copy dish');
      }
    } catch (err) {
      console.error('Copy dish error:', err);
      toast.error('Failed to copy dish');
    } finally {
      setCopyingDish(false);
    }
  };

  const branchLabel = selectedBranch?.name || 'All Outlets';

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAFAFA', p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
          color: 'white',
          boxShadow: `0 8px 32px ${colors.primary.main}20`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <Box sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 3,
          alignItems: 'center'
        }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <IconButton
                onClick={() => navigate(`/dish-access/${menuId}`)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', md: '2rem' },
                  background: 'linear-gradient(45deg, #FFFFFF 30%, rgba(255,255,255,0.8) 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {category?.name || 'Category'} Dishes
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Chip
                icon={<StoreIcon />}
                label={`Branch: ${branchLabel}`}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Chip
                icon={<CategoryIcon />}
                label={`Category: ${category?.name || 'Category'}`}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 500,
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 600,
                lineHeight: 1.6,
                fontSize: '1rem'
              }}
            >
              Manage dishes in the {category?.name?.toLowerCase() || 'category'} category.
              Add, edit, or remove dishes as needed.
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: 'flex-start',
            minWidth: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              startIcon={<DishIcon />}
              onClick={() => {
                closeDishDialog();
                setDishDialogOpen(true);
              }}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.4)',
                borderWidth: 2,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              Add Dish
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {dishes.length === 0 ? (
        <Box sx={{
          py: { xs: 6, md: 8 },
          textAlign: 'center',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'grey.100'
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#F5F5F5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <DishIcon sx={{ fontSize: 40, color: '#607D8B' }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#37474F',
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}
          >
            No dishes yet
          </Typography>
          <Typography
            sx={{
              mb: 4,
              maxWidth: 500,
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1rem',
              color: '#607D8B'
            }}
          >
            Start by adding a dish to this category.
          </Typography>
          <Button
            variant="contained"
            startIcon={<DishIcon />}
            onClick={() => setDishDialogOpen(true)}
            sx={{
              backgroundColor: '#607D8B',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(96,125,139,0.3)',
              '&:hover': {
                backgroundColor: '#546E7A',
                boxShadow: '0 4px 12px rgba(96,125,139,0.4)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Add Your First Dish
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Bulk Actions Header */}
          <Card sx={{
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #E8E8E8',
            backgroundColor: 'white'
          }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDishes.size === dishes.length && dishes.length > 0}
                        indeterminate={selectedDishes.size > 0 && selectedDishes.size < dishes.length}
                        onChange={toggleSelectAll}
                        sx={{
                          color: '#607D8B',
                          '&.Mui-checked': {
                            color: '#607D8B',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#37474F' }}>
                        Select All ({selectedDishes.size} of {dishes.length} selected)
                      </Typography>
                    }
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBulkActivate}
                    disabled={selectedDishes.size === 0 || bulkOperationInProgress}
                    startIcon={bulkOperationInProgress ? <CircularProgress size={16} /> : null}
                    sx={{
                      borderColor: '#4CAF50',
                      color: '#4CAF50',
                      '&:hover': {
                        borderColor: '#45a049',
                        backgroundColor: 'rgba(76, 175, 80, 0.04)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#E0E0E0',
                        color: '#9E9E9E',
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                    }}
                  >
                    Activate Selected
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBulkDeactivate}
                    disabled={selectedDishes.size === 0 || bulkOperationInProgress}
                    startIcon={bulkOperationInProgress ? <CircularProgress size={16} /> : null}
                    sx={{
                      borderColor: '#F44336',
                      color: '#F44336',
                      '&:hover': {
                        borderColor: '#d32f2f',
                        backgroundColor: 'rgba(244, 67, 54, 0.04)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#E0E0E0',
                        color: '#9E9E9E',
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                    }}
                  >
                    Deactivate Selected
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={openBulkCategoryDialog}
                    disabled={selectedDishes.size === 0 || bulkOperationInProgress || bulkCategoryInProgress}
                    startIcon={<CopyIcon fontSize="small" />}
                    sx={{
                      backgroundColor: '#1976D2',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#1565C0',
                      },
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                    }}
                  >
                    Copy / Move Selected
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Dish List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {dishes.map((dish) => (
            <Card
              key={dish.id}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                border: '1px solid',
                borderColor: selectedDishes.has(dish.id) ? '#607D8B' : '#E8E8E8',
                backgroundColor: selectedDishes.has(dish.id) ? 'rgba(96, 125, 139, 0.02)' : 'white',
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  borderColor: selectedDishes.has(dish.id) ? '#607D8B' : '#D0D0D0'
                }
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  {/* Selection Checkbox */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Checkbox
                      checked={selectedDishes.has(dish.id)}
                      onChange={() => toggleDishSelection(dish.id)}
                      sx={{
                        color: '#607D8B',
                        '&.Mui-checked': {
                          color: '#607D8B',
                        },
                      }}
                    />
                  </Box>

                  {/* Left section: Dish info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#2C3E50',
                            fontSize: '1rem',
                            margin: 0
                          }}
                        >
                          {dish.name}
                        </Typography>
                        {dish.code && (
                          <Typography variant="body2" sx={{ color: '#607D8B', fontSize: '0.82rem', mt: 0.5 }}>
                            Code: {dish.code}
                          </Typography>
                        )}
                      </Box>
                      {/* Veg/Non-veg indicator */}
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '2px',
                          border: '2px solid',
                          borderColor: dish.is_veg ? '#4CAF50' : '#FF5722',
                          backgroundColor: dish.is_veg ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 87, 34, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: dish.is_veg ? '#4CAF50' : '#FF5722'
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ color: '#7F8C8D', fontSize: '0.9rem' }}>
                        <span style={{ fontWeight: 500, color: '#607D8B' }}>₹{dish.selling_price_with_gst || dish.price}</span>
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#95A5A6', fontSize: '0.85rem' }}>
                        {category?.name}
                      </Typography>
                      {dish.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#95A5A6',
                            fontSize: '0.85rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            maxWidth: '400px'
                          }}
                        >
                          {dish.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Right section: Actions and toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Copy Dish">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCopyDishDialog(dish);
                          }}
                          sx={{
                            p: 0.75,
                            backgroundColor: '#E3F2FD',
                            color: '#1976D2',
                            fontSize: 16,
                            '&:hover': {
                              backgroundColor: '#1976D2',
                              color: 'white'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <CopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Dish">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDishDialog(dish);
                          }}
                          sx={{
                            p: 0.75,
                            backgroundColor: '#F5F5F5',
                            color: '#607D8B',
                            fontSize: 16,
                            '&:hover': {
                              backgroundColor: '#607D8B',
                              color: 'white'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Dish">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDish(dish.id);
                          }}
                          disabled={deletingItem === dish.id}
                          sx={{
                            p: 0.75,
                            backgroundColor: '#FFEBEE',
                            color: '#F44336',
                            fontSize: 16,
                            '&:hover': {
                              backgroundColor: '#F44336',
                              color: 'white'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        >
                          {deletingItem === dish.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="inherit" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Status toggle */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={dish.is_available}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleDish(dish);
                          }}
                          disabled={togglingItem === dish.id}
                          size="small"
                          sx={{
                            ml: 1,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4CAF50',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#4CAF50'
                              }
                            },
                            '& .MuiSwitch-switchBase': {
                              color: '#BDC3C7',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#BDC3C7'
                              }
                            }
                          }}
                        />
                      }
                      label=""
                        />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          </Box>
        </Box>
      )}

      {/* Add/Edit Dish Dialog */}
      <Dialog
        open={dishDialogOpen}
        onClose={closeDishDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          p: 3,
          pb: 2,
          backgroundColor: '#FAFAFA',
          borderBottom: '1px solid #E0E0E0',
          fontWeight: 700,
          fontSize: '1.1rem',
          color: '#37474F'
        }}>
          {editingDish ? 'Edit Dish' : 'Add New Dish'}
        </DialogTitle>

        <DialogContent sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}>
          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: '#607D8B',
              fontSize: '0.9rem',
              mb: 1
            }}
          >
            {editingDish 
              ? 'Update dish details to keep your menu current.'
              : 'Add a new dish with essential information for your menu.'}
          </Typography>

          {/* Dish Name */}
          <TextField
            label="Dish Name"
            value={newDish.name}
            onChange={(e) => setNewDish({ ...newDish, name: e.target.value })}
            placeholder="e.g., Butter Chicken"
            fullWidth
            autoFocus
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.95rem',
                '&:hover fieldset': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#607D8B',
                  borderWidth: '2px'
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#607D8B',
              },
            }}
          />

          {/* Dish Code / Product Code */}
          <TextField
            label="Dish / Product Code"
            value={newDish.code}
            onChange={(e) => setNewDish({ ...newDish, code: e.target.value })}
            placeholder="e.g., DK-001"
            fullWidth
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.95rem',
                '&:hover fieldset': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#607D8B',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#607D8B',
              },
            }}
          />

          {/* Category */}
          <FormControl fullWidth variant="outlined" size="medium" sx={{ mt: 1 }}>
            <InputLabel id="dish-category-label">Category</InputLabel>
            <Select
              labelId="dish-category-label"
              value={newDish.category || ''}
              label="Category"
              onChange={(e) => setNewDish({ ...newDish, category: e.target.value })}
              disabled={editingDish}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#CED4DA',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#607D8B',
                },
              }}
            >
              {categoryOptions.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Menu Types */}
          <FormControl fullWidth variant="outlined" size="medium" sx={{ mt: 1 }}>
            <InputLabel id="dish-menu-label">Menu Types</InputLabel>
            <Select
              labelId="dish-menu-label"
              multiple
              value={newDish.menuTypes || []}
              onChange={(e) => setNewDish({ ...newDish, menuTypes: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })}
              label="Menu Types"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((menuIdValue) => {
                    const menuOption = menuOptions.find((menu) => menu.id === menuIdValue);
                    return (
                      <Chip
                        key={menuIdValue}
                        label={menuOption?.name || `Menu ${menuIdValue}`}
                        size="small"
                        sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                      />
                    );
                  })}
                </Box>
              )}
              disabled={editingDish}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#CED4DA',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#607D8B',
                },
              }}
            >
              {menuOptions.map((menu) => (
                <MenuItem key={menu.id} value={menu.id}>
                  {menu.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Price Input */}
          <TextField
            label="Price"
            type="number"
            value={newDish.price}
            onChange={(e) => setNewDish({ ...newDish, price: e.target.value })}
            placeholder="0.00"
            fullWidth
            variant="outlined"
            size="medium"
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#607D8B' }}>
                    ₹
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.95rem',
                '&:hover fieldset': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#607D8B',
                  borderWidth: '2px'
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#607D8B',
              },
            }}
          />

          {/* Veg/Non-Veg Toggle */}
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: '#37474F',
                mb: 1.5,
                fontSize: '0.9rem'
              }}
            >
              Dish Type
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={newDish.is_veg ? 'veg' : 'non-veg'}
                onChange={(e) => setNewDish({ ...newDish, is_veg: e.target.value === 'veg' })}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="veg"
                  control={
                    <Radio
                      sx={{
                        color: '#4CAF50',
                        '&.Mui-checked': {
                          color: '#4CAF50',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          backgroundColor: '#4CAF50',
                          border: '2px solid #4CAF50'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Vegetarian
                      </Typography>
                    </Box>
                  }
                  sx={{ color: '#37474F' }}
                />
                <FormControlLabel
                  value="non-veg"
                  control={
                    <Radio
                      sx={{
                        color: '#FF5722',
                        '&.Mui-checked': {
                          color: '#FF5722',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          backgroundColor: '#FF5722',
                          border: '2px solid #FF5722'
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Non-Vegetarian
                      </Typography>
                    </Box>
                  }
                  sx={{ color: '#37474F' }}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Description */}
          <TextField
            label="Description (Optional)"
            value={newDish.description}
            onChange={(e) => setNewDish({ ...newDish, description: e.target.value })}
            placeholder="Brief description of the dish"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '0.95rem',
                '&:hover fieldset': {
                  borderColor: '#607D8B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#607D8B',
                  borderWidth: '2px'
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem'
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#607D8B',
              },
            }}
          />

          {(!selectedBranch?.id || selectedBranch?.isAllOutlets) && !editingDish && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 1.5,
                backgroundColor: '#FFF3E0',
                color: '#E65100',
                fontSize: '0.85rem',
                '& .MuiAlert-icon': {
                  color: '#E65100',
                },
              }}
            >
              Please select a specific branch to add a dish.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 2,
          display: 'flex',
          gap: 1.5,
          borderTop: '1px solid #E0E0E0'
        }}>
          <Button
            onClick={closeDishDialog}
            variant="text"
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={editingDish ? handleEditDish : handleCreateDish}
            variant="contained"
            disabled={savingDish || !newDish.name.trim() || !newDish.price || ((!selectedBranch?.id || selectedBranch?.isAllOutlets) && !editingDish)}
            sx={{
              backgroundColor: '#607D8B',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 1.5,
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#546E7A',
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E'
              }
            }}
          >
            {savingDish ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Saving...
              </Box>
            ) : (
              editingDish ? 'Update' : 'Add Dish'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Dish Dialog */}
      <Dialog
        open={copyDishDialogOpen}
        onClose={closeCopyDishDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#F5F5F5',
          color: '#37474F',
          fontWeight: 600,
          fontSize: '1.25rem',
          pb: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CopyIcon sx={{ color: '#1976D2' }} />
            Copy Dish
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {copyDishSource && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#E3F2FD',
                  color: '#0D47A1',
                  '& .MuiAlert-icon': {
                    color: '#0D47A1',
                  },
                }}
              >
                <strong>Source Dish:</strong> {copyDishSource.name}
              </Alert>
            )}

            <FormControl fullWidth variant="outlined">
              <InputLabel sx={{ color: '#607D8B', '&.Mui-focused': { color: '#1976D2' } }}>
                Target Menu Type
              </InputLabel>
              <Select
                value={copyDishTargetMenu}
                onChange={(e) => setCopyDishTargetMenu(e.target.value)}
                label="Target Menu Type"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                }}
              >
                {availableMenusForDishCopy.map((menu) => (
                  <MenuItem key={menu.id} value={menu.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DishIcon sx={{ fontSize: 18, color: '#607D8B' }} />
                      {menu.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                backgroundColor: '#E3F2FD',
                color: '#0D47A1',
                '& .MuiAlert-icon': {
                  color: '#0D47A1',
                },
              }}
            >
              This will create a copy of the dish in the selected menu type. The dish will maintain its category consistency - if the category exists in the target menu, it will be placed there; otherwise, a matching category will be created automatically.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={closeCopyDishDialog}
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyDish}
            variant="contained"
            disabled={copyingDish || !copyDishTargetMenu}
            sx={{
              backgroundColor: '#1976D2',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
              '&:hover': {
                backgroundColor: '#1565C0',
                boxShadow: '0 4px 12px rgba(25,118,210,0.4)',
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {copyingDish ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Copying...
              </Box>
            ) : (
              'Copy Dish'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkCategoryDialogOpen}
        onClose={closeBulkCategoryDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#F5F5F5',
          color: '#37474F',
          fontWeight: 600,
          fontSize: '1.25rem',
          pb: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CopyIcon sx={{ color: '#1976D2' }} />
            Copy Selected Dishes to Another Menu
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <Stack spacing={3} mt={1}>
            <Alert severity="info" sx={{ borderRadius: 2, backgroundColor: '#E3F2FD', color: '#0D47A1', '& .MuiAlert-icon': { color: '#0D47A1' } }}>
              {selectedDishes.size} dish(es) selected from "{category?.name}" category. Choose a target menu type to copy them to.
            </Alert>

            <FormControl fullWidth variant="outlined">
              <InputLabel sx={{ color: '#607D8B', '&.Mui-focused': { color: '#1976D2' } }}>
                Target Menu Type
              </InputLabel>
              <Select
                value={bulkCategoryTargetMenu}
                onChange={(e) => setBulkCategoryTargetMenu(e.target.value)}
                label="Target Menu Type"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                }}
              >
                {availableMenusForBulkCategoryOperation.map((menu) => (
                  <MenuItem key={menu.id} value={menu.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DishIcon sx={{ fontSize: 18, color: '#607D8B' }} />
                      {menu.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ borderRadius: 2, backgroundColor: '#FFF3E0', color: '#E65100', '& .MuiAlert-icon': { color: '#E65100' } }}>
              <strong>Automatic Category Matching:</strong> Dishes will be copied to a category named "{category?.name}" in the target menu. If this category doesn't exist, it will be created automatically. Duplicate dishes (same name in target menu) will be skipped. You cannot copy dishes within the same menu type.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={closeBulkCategoryDialog}
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkCategoryOperation}
            variant="contained"
            disabled={bulkCategoryInProgress || !bulkCategoryTargetMenu}
            sx={{
              backgroundColor: '#1976D2',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
              '&:hover': {
                backgroundColor: '#1565C0',
                boxShadow: '0 4px 12px rgba(25,118,210,0.4)',
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {bulkCategoryInProgress ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Copying...
              </Box>
            ) : (
              'Copy Dishes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryDishes;